<script>
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  ColumnSeries,
  Category,
  DataLabel,
  Tooltip,
  Legend,
  Highlight,
} from '@syncfusion/ej2-vue-charts';

import {
  resolutionReasonsMetricColors,
  smallest_Sizes,
  smaller_1_Sizes,
  smaller_2_Sizes,
  medium_1_Sizes,
  medium_2_Sizes,
  biggerSizes,
} from '../../../../../../../../../shared/helpers/CustomDashboardsHelper.js';

export default {
  components: {
    ChartComponent,
    SeriesCollectionDirective,
    SeriesDirective,
  },
  provide: {
    chart: [ColumnSeries, Legend, DataLabel, Category, Tooltip, Highlight],
  },
  props: {
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    currentWidth: {
      type: Number,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    dateRange: {
      type: Object,
      required: true,
    },
    showDetailedMsgsColChart: {
      type: Boolean,
      default: false,
    },
    groupByInbox: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      chartArea: {
        border: {
          width: 0,
        },
      },
      primaryYAxis: {
        title: this.$t(
          'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.COUNT'
        ),
        labelFormat: '{value}',
      },
      legendSettings: {
        visible: true,
        position: 'Bottom',
      },
      marker: {
        dataLabel: {
          visible: false,
          position: 'Top',
        },
      },
      tooltip: {
        enable: true,
      },
    };
  },

  computed: {
    // Computed property to determine if the data interval is daily
    isDailyData() {
      return (
        this.dateRange &&
        this.dateRange.to - this.dateRange.from <= 7 * 24 * 60 * 60
      );
    },
    // Define the X axis dynamically based on the data interval
    primaryXAxis() {
      return {
        valueType: 'Category',
        labelIntersectAction: 'Rotate45',
        majorGridLines: { width: 0 },
        labelFormat: this.isDailyData ? 'dd MMM' : 'MMM', // Adjust the label format dynamically
      };
    },
    // Series data for the chart, based on different resolution types
    seriesData() {
      return [
        {
          name: this.objectiveAchievedLabel(),
          data: this.getChartData('objective_achieved'),
          color: resolutionReasonsMetricColors.objectiveAchieved,
        },
        {
          name: this.objectiveNotAchievedLabel(),
          data: this.getChartData('objective_not_achieved'),
          color: resolutionReasonsMetricColors.objectiveNotAchieved,
        },
        {
          name: this.doubtLabel(),
          data: this.getChartData('doubt'),
          color: resolutionReasonsMetricColors.doubt,
        },
        {
          name: this.otherLabel(),
          data: this.getChartData('other'),
          color: resolutionReasonsMetricColors.other,
        },
      ];
    },
    // Adjust responsive sizes based on screen width and widget size
    responsiveSizes() {
      // FULL HD (1920x1080)
      if (window.innerWidth >= 1750) {
        if (this.sizeY > 1) {
          if (this.currentWidth < 600) return medium_1_Sizes;
          if (this.currentWidth < 1100) return medium_2_Sizes;
          return biggerSizes;
        }
        return smaller_2_Sizes;
      }
      // NOTEBOOKS (1600 X 900)
      if (window.innerWidth >= 1400) {
        if (this.sizeY > 1) {
          if (this.currentWidth < 550) return smaller_2_Sizes;
          return medium_2_Sizes;
        }
        return smaller_1_Sizes;
      }

      if (this.sizeY > 1) {
        if (this.currentWidth < 250) {
          const sizes = { ...smallest_Sizes, containerPaddingTop: '80' };
          return sizes;
        }
        if (this.currentWidth < 400) return smaller_2_Sizes;
        return medium_1_Sizes;
      }
      return smallest_Sizes;
    },
    noDataFound() {
      return this.seriesData.every(seriesItem =>
        seriesItem.data.every(item => item.value === 0)
      );
    },
  },
  methods: {
    // Generates a unique ID for the chart container
    generateUniqId() {
      return parseInt(Date.now() * Math.random().toFixed(5), 10);
    },
    // Fetches and processes chart data based on the resolution type
    getChartData(type) {
      const data = [];
      if (!this.data.monthly_data) return data;

      // Iterates over each set of monthly or daily data
      this.data.monthly_data.forEach(intervalData => {
        // Initializes the count value as 0 for all intervals
        let count = 0;

        // If data exists, calculate the total count for the provided resolution type
        if (intervalData.data && Array.isArray(intervalData.data)) {
          count = intervalData.data
            .filter(resolution => resolution.type === type)
            .reduce((sum, resolution) => sum + resolution.count, 0);
        }

        // Ensures all intervals are represented, even without data
        data.push({
          date: intervalData.month || intervalData.date, // Name of the month or date
          value: count, // Total count for the resolution type
        });
      });

      return data;
    },
    tooltipRender(args) {
      args.text = `${args.point.x} : <b>${args.point.y}</b>`;
    },
    objectiveAchievedLabel() {
      return this.$t(
        'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OBJECTIVE_ACHIEVED'
      );
    },
    objectiveNotAchievedLabel() {
      return this.$t(
        'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OBJECTIVE_NOT_ACHIEVED'
      );
    },
    doubtLabel() {
      return this.$t('CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.DOUBT');
    },
    otherLabel() {
      return this.$t('CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OTHER');
    },
  },
};
</script>

<template>
  <div v-if="noDataFound" class="text-xs">
    {{ $t('CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.NO_RESOLUTION') }}
  </div>
  <div
    v-else
    class="column-chart-container flex justify-center items-center"
    :style="{ 'padding-top': responsiveSizes.containerPaddingTop }"
  >
    <div align="center">
      <ChartComponent
        :id="`chartcontainer-${generateUniqId()}`"
        :primary-x-axis="primaryXAxis"
        :primary-y-axis="primaryYAxis"
        :chart-area="chartArea"
        :legend-settings="legendSettings"
        :tooltip="tooltip"
        :tooltip-render="tooltipRender"
        :width="responsiveSizes.containerWidth"
        :height="responsiveSizes.containerHeight"
      >
        <SeriesCollectionDirective>
          <SeriesDirective
            v-for="(seriesItem, index) in seriesData"
            :key="index"
            :data-source="seriesItem.data"
            type="Column"
            x-name="date"
            y-name="value"
            :name="seriesItem.name"
            :marker="marker"
            :fill="seriesItem.color"
            legend-shape="Circle"
          />
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  </div>
</template>

<style scoped>
.column-chart-container {
  @apply text-slate-700;
}
</style>
